import React from 'react'

import { Divider } from '@awazevr/design-system.components.divider'
import { Stack } from '@awazevr/design-system.components.stack'
import { Wishlists } from './components/Wishlists'
import {
  StyledPropertyCardNewDesign,
  StyledContentAnchor,
  StyledPricingContainer,
  StyledRatingsAndProofGroup,
} from './PropertyCardNewDesign.styles'
import { Carousel } from './components/Carousel'
import { Ratings } from './components/Ratings'
import { SocialProof } from './components/SocialProof'
import { CalloutBadge } from './components/CalloutBadge'
import { Occupancy } from './components/Occupancy'
import { PropertyInfo } from './components/PropertyInfo'
import { DepositBanner } from './components/DepositBanner'
import { Pricing } from './components/Pricing'
import { Availability } from './components/Availability'
import { KSPs } from './components/KSPs'
import { usePublicAssetUrl } from '../../utils/helpers'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'
import type { PropertyCardProps } from './PropertyCardNewDesign.types'
import { getPropertyCardDisplayRules } from './propertyCardDisplayRules'

export type {
  PropertyCardProps,
  PropertyCardV2Variant,
} from './PropertyCardNewDesign.types'

export const PropertyCardNewDesign: React.FC<PropertyCardProps> = ({
  brand,
  propertyCardV2Variant,
  code,
  images,
  wasPrice,
  fromPrice,
  startDate,
  onCarouselInteractCallback,
  onPropertyLinkClick,
  activeDepositScheme,
  lengthOfStay,
  propertyLocation,
  propertyName,
  propertyType,
  translations,
  grade,
  guestRating,
  property,
  keySellingPoints,
  price,
  pageUrl,
  hasPetsFree,
  hasCallout,
  siteSearch,
  datedSearch,
  wishlistButton,
  urgencyMessaging,
}) => {
  const { formatMessage } = useIntlContext()

  let gradeImagePath

  switch (brand) {
    case 'cottages.com':
      gradeImagePath = '/brand/property-card-icons/CDC.svg'
      break
    case 'hoseasons':
      gradeImagePath = '/brand/property-card-icons/HOS.svg'
      break
    default:
      gradeImagePath = '/brand/property-card-icons/NOV.svg'
      break
  }

  const displayRules = getPropertyCardDisplayRules({
    brand,
    datedSearch,
    fromPrice,
    price,
    siteSearch,
  })

  const gradeImageUrl = usePublicAssetUrl(gradeImagePath)

  const cardAriaLabel = `${propertyName || 'Property'}${
    propertyLocation ? ` - ${propertyLocation}` : ''
  }`

  return (
    <StyledPropertyCardNewDesign
      aria-label={cardAriaLabel}
      href={pageUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onPropertyLinkClick}
      data-property-card-v2-variant={propertyCardV2Variant}
      propertyCardV2Variant={propertyCardV2Variant}
    >
      {/* Media Section */}
      <Carousel
        images={images}
        uniqueId={code}
        propertyCardV2Variant={propertyCardV2Variant}
        onCarouselInteractCallback={onCarouselInteractCallback}
        overlays={
          <>
            <Wishlists
              wishlistButton={wishlistButton}
              label={formatMessage(
                wishlistButton?.isPropertyInWishlist
                  ? {
                      id: 'wishlist.common.saved',
                      defaultMessage: 'Saved',
                    }
                  : {
                      id: 'wishlist.common.save',
                      defaultMessage: 'Save',
                    },
              )}
            />
            <CalloutBadge
              visible={hasCallout}
              text={translations.calloutBadge}
            />
          </>
        }
      />

      <StyledContentAnchor>
        {/* Prices Section */}
        <Stack spacing={8}>
          <StyledPricingContainer
            direction={displayRules.useDatedLayout ? 'row-reverse' : 'column'}
          >
            <Availability
              startDate={startDate}
              lengthOfStay={lengthOfStay}
              nightsLabel={translations?.nights}
              fromLabel={translations?.from}
              hasFromPrice={displayRules.hasFromPrice}
              showStartDate={displayRules.showStartDate}
              showFromLabel={displayRules.showFromLabel}
            />
            <Pricing
              fromPrice={fromPrice}
              price={price}
              wasPrice={wasPrice}
              formatCurrency={translations.formatCurrency}
            />
          </StyledPricingContainer>

          <KSPs
            variant="tablet"
            includedExtras={property.includedExtras}
            keySellingPoints={keySellingPoints}
            hasPetsFree={hasPetsFree}
            petsFreeLabel={translations.petsFree}
            code={code}
          />
        </Stack>

        <Divider margin={16} />

        <Stack direction="column" spacing={[8, 8, 8]}>
          <PropertyInfo
            propertyType={propertyType}
            propertyLocation={propertyLocation}
            propertyName={propertyName}
            showPropertyType={displayRules.showPropertyType}
          />

          <StyledRatingsAndProofGroup>
            <Ratings
              grade={grade}
              guestRating={guestRating}
              translations={{
                grade: translations?.grade,
                guestRating: translations?.guestRating,
                outOf: translations?.outOf,
              }}
              idPrefix={code}
            />

            <SocialProof
              visible={urgencyMessaging}
              text={translations.urgencyBadge}
            />
          </StyledRatingsAndProofGroup>

          <KSPs
            variant="mobile"
            includedExtras={property.includedExtras}
            keySellingPoints={keySellingPoints}
            hasPetsFree={hasPetsFree}
            petsFreeLabel={translations.petsFree}
            code={code}
          />

          {displayRules.showPropertySpecs && <Divider margin={16} />}
        </Stack>

        {/* Property Specs */}

        {displayRules.showPropertySpecs && (
          <>
            <Divider margin={16} isVertical={true} />
            <Occupancy
              sleeps={property.sleeps}
              bedrooms={property.bedrooms}
              bathrooms={property.bathrooms}
              pets={property.pets}
              translations={translations.propertySpecTranslations}
            />
          </>
        )}
      </StyledContentAnchor>

      {/* Low Deposit */}
      <DepositBanner
        activeDepositScheme={activeDepositScheme}
        translations={translations.depositBanner}
        formatCurrency={translations.formatCurrency}
        showTermsMessage={displayRules.showDepositTerms}
      />
    </StyledPropertyCardNewDesign>
  )
}

export default PropertyCardNewDesign
